import type { GlobalStateCommandFactory } from 'components/GlobalState/GlobalStateModel';
import { isLoggedOutError } from 'components/GlobalState/commands/helpers/isLoggedOutError';
import { refreshTokenExpirationLocalStorage } from 'components/GlobalState/refreshTokenExpirationLocalStorage';
import { removeAllNotifications } from 'containers/Toaster/actions';
import { type AuthResponseData, saveAuth } from './saveAuth';

export const refreshTokens: GlobalStateCommandFactory<boolean | undefined> =
  (cmdContext, dispatch) => async (doNotLogout) => {
    const { refreshingTokensPromise } = cmdContext.getState();
    // if refreshing token is already running, don't fire anoher request but wait for it
    // as anyone waiting for this command to finish may (and probably will) rely on refreshed tokens.
    if (refreshingTokensPromise !== null) {
      return refreshingTokensPromise;
    }

    const showLogoutNotificationIfNeeded = () => {
      const refreshTokenExpiresAt =
        refreshTokenExpirationLocalStorage.getValue();
      // refreshTokenExpiresAt can be null if user logged out deliberately.
      // We don't want to show notification in this case.
      // If it is not null, it means that user was logged by browser or server
      if (refreshTokenExpiresAt !== null) {
        cmdContext.showNotification({
          type: 'info',
          message: cmdContext.getState().msg.request.invalidRefreshToken,
        });
        refreshTokenExpirationLocalStorage.setValue(null);
      }
    };

    const doRefresh = async () => {
      try {
        const responseData = await cmdContext.api.request<AuthResponseData>({
          url: '/auth/token',
          method: 'POST',
        });
        dispatch(saveAuth, responseData);
      } catch (e) {
        if (isLoggedOutError(e)) {
          // set user to logged out here because not all places where refreshTokens is called are able to set it
          cmdContext.updateState((draft) => {
            draft.loginStatus = 'LOGGED_OUT';
          });
          if (!doNotLogout) {
            cmdContext.navigatePushPath('/logout');

            // We can have several request which fail on expired refresh token
            cmdContext.reduxDispatch(removeAllNotifications());

            showLogoutNotificationIfNeeded();
          }
        }
        throw e;
      }
    };

    try {
      const refreshPromise = doRefresh();
      cmdContext.updateState((draft) => {
        draft.refreshingTokensPromise = refreshPromise;
      });
      return await refreshPromise;
    } finally {
      cmdContext.updateState((draft) => {
        draft.refreshingTokensPromise = null;
      });
    }
  };
